import {
  Box,
  Button,
  Hidden,
  List,
  ListItem,
  makeStyles,
  Paper,
  Typography,
  useMediaQuery,
  useTheme,
} from "@material-ui/core";
import ArrowBackIosIcon from "@material-ui/icons/ArrowBackIos";
import { TEXT } from "Clients/ACCB/ACCBTheme";
import { removeSessionStorage } from "features/tiac_header_waiting/TiacAppHeader";
import React, { useEffect, useRef, useState } from "react";
import { FormattedMessage } from "react-intl";
import { useHistory, useLocation } from "react-router-dom";
import { getWindowDimension } from "utils/responsiveUtils";
import Overview from "./Overview";
import CreateNewMatter from "../Form/CreateNewMatter";
import Arbitrators from "./Arbitrators";
import ReportContainer from "./Report/ReportContainer";
import DailyProceeding from "./DailyProceeding";
import OrderDrafting from "./OrderDrafting/OrderDrafting";
import BookHearingRoom from "./BookHearingRoom";
import { ADMIN_TAB_REDIRECT_KEY } from "./adminTabNavigation";
import AdjournmentsTab from "./AdjournmentsTab";
import RescheduleTab from "./RescheduleTab";
import CauseList from "Clients/ACCB/Admin/CauseList";
import Notices from "./Notices";
import MasterCis from "./Master";
import Scrutiny from "../Components/Scrutiny/Scrutiny";
import useBACPermissions from "../hooks/usePermissions";
import useViewportFill from "../hooks/useViewportFill";
import FeeCalculator from "../Components/FeeCalculator/FeeCalculator";
import FinancialContainer from "../Components/Financial/FinancialContainer";
import GenerateVCLink from "../Components/GenerateVCLink";
import EmailsDashboard from "./EmailsDashboard";
import MeetingLinks from "./MeetingLinks";


const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    width: "100%",
    // Height comes from useViewportFill (../hooks) - the page itself must not grow
    // past the viewport, or the sidebar scrolls away with the content.
    overflow: "hidden",
    // flexDirection: (props) => (props.smallScreen ? "column" : "row"),
  },
  sidebar: {
    background: theme.palette.primary.main,
    width: "23rem",
    overflowY: "auto",
    [theme.breakpoints.down("sm")]: {
      width: "100%",
    },
  },
  sidebarItem: {
    //#A4A6B3;
    fontSize: "1rem",
    padding: "0.5rem 1rem 0.5rem 3rem",
    color: "white",
    letterSpacing: "0.2px",
    [theme.breakpoints.down("md")]: {
      fontSize: "1rem",
      padding: "0.5rem 1rem 0.5rem 2.5rem",
    },
    [theme.breakpoints.down("xs")]: {
      fontSize: "0.875rem",
      padding: "0.4rem 0 0.4rem 3rem",
    },
    // "&:hover": {
    //   backgroundColor: "#ddcdb52b",
    // }
  },
  sidebarHeading: {
    padding: "15px 1.25rem 30px",
    fontSize: "18px",
    color: "rgba(238, 238, 238, 0.7)",
    fontWeight: "bold",
  },
  selected: {
    backgroundColor: "#d3a35c",
    // opacity: 0.08,
    // color:TEXT.onDark,
    borderLeft: "2px solid #FFFFFF",
    "&:hover": {
      backgroundColor: "#d3a35c",
    }
  },
  // Every tab renders inside this shell, so each page title starts at the same
  // x and y regardless of what the panel does inside. Panels must not add their
  // own outer padding or their own page heading.
  panel: {
    padding: theme.spacing(3),
    textAlign: "left",
    width: "100%",
    boxSizing: "border-box",
  },
  // Heading row: the page title, plus the signed-in user's name on the right
  // for the Overview (View Case) tab.
  panelTitleRow: {
    display: "flex",
    alignItems: "baseline",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: theme.spacing(1, 2),
    marginBottom: theme.spacing(3),
  },
  panelTitle: {
    fontSize: "1.5rem",
    fontWeight: 600,
    lineHeight: 1.3,
    color: TEXT.strong,
    margin: 0,
    [theme.breakpoints.down("sm")]: {
      fontSize: "1.25rem",
    },
  },
  panelUser: {
    fontSize: "0.95rem",
    fontWeight: 600,
    color: TEXT.secondary,
  },
  body: {
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
    minHeight: 0,
    padding: "0",
    overflow: "auto",
    width: "100%",
    textAlign: "left",
  },
  body2: {
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
    minHeight: 0,
    minWidth: 0,
    padding: "0",
    overflow: "hidden",
    width: "100%",
    textAlign: "left",
  },
}));

export default function AdminDashboard({ profile }) {
  const classes = useStyles();
  // Staff access: which sidebar sections this user may see.
  const { can } = useBACPermissions();

  let ls = sessionStorage.getItem("tiac_sidebar_step");
  let mb = sessionStorage.getItem("tiac_sidebar_mobilestep");

  const [step, setStep] = useState(mb ? mb : 0);

  const [selected, setSelected] = useState(
    ls ? ls : getWindowDimension().width < 960 ? 0 : 1
  );

  // Deep link from Order Drafting: /subadmin?book_order_drafting=true&date=…&
  // case_number=…&year=…&stage=…&prefix=… opens the Book Hearing Room tab (10)
  // pre-filled with that case, stage and date so the arbitrator can book there.
  const location = useLocation();
  const [bookingPrefill, setBookingPrefill] = useState(null);
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("book_order_drafting") === "true") {
      setBookingPrefill({
        case_number: params.get("case_number") || "",
        year: params.get("year") || "",
        stage: params.get("stage") || "",
        date: params.get("date") || "",
        prefix: params.get("prefix") || "AC",
      });
      setSelected(10);
    }
  }, [location.search]);

  // Cross-tab redirect (see ./adminTabNavigation): another screen opened a tab
  // via navigateToAdminTab, with optional data for it in location.state.
  const history = useHistory();
  const [tabRedirect, setTabRedirect] = useState(null);
  useEffect(() => {
    const redirect = location.state?.[ADMIN_TAB_REDIRECT_KEY];
    if (!redirect?.tab) return;
    setTabRedirect(redirect);
    setSelected(redirect.tab);
    setStep(1);
    sessionStorage.setItem("tiac_sidebar_step", redirect.tab);
    sessionStorage.setItem("tiac_sidebar_mobilestep", 1);
    // Consume it, so a reload doesn't replay the jump.
    history.replace({ pathname: location.pathname, search: location.search });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  // Leaving the redirected tab drops its data, so reopening that tab later from
  // the sidebar starts clean instead of replaying the old prefill.
  useEffect(() => {
    if (tabRedirect && String(selected) !== String(tabRedirect.tab)) {
      setTabRedirect(null);
    }
  }, [selected, tabRedirect]);

  const rootRef = useRef(null);
  const viewportHeight = useViewportFill(rootRef);

  // Desktop shows the sidebar beside the open tab; mobile shows one at a time,
  // switched by `step`. There is deliberately ONE Body for both: rendering
  // separate desktop and mobile trees (via <Hidden>) remounted the open tab
  // whenever the width crossed the md breakpoint — e.g. browser zoom to 175% —
  // throwing away its state, such as an Order Drafting editor mid-draft.
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"), { noSsr: true });
  const showSidebar = !isMobile || String(step) === "0";
  const showBody = !isMobile || String(step) === "1";

  return (
    <Box ref={rootRef} className={classes.root} style={{ height: viewportHeight }}>
      {showSidebar && (
        <Sidebar
          can={can}
          selected={selected}
          setSelected={setSelected}
          setStep={setStep}
        />
      )}
      {/* Hidden rather than unmounted on the mobile menu step, so the tab keeps
          its state when the menu is shown. */}
      <Body
        hidden={!showBody}
        selected={selected}
        profile={profile}
        setSelected={setSelected}
        setStep={setStep}
        bookingPrefill={bookingPrefill}
        tabRedirect={tabRedirect}
      />
    </Box>
  );
}

// One title per tab, rendered by the shell below so every page heading lands in
// the same place. Keyed by the same `selected` index the sidebar sets.
const PANEL_TITLES = {
  0: "Overview",
  1: "Overview",
  2: "Register New Case",
  3: "Notices",
  4: "Valuation of Fee",
  5: "Arbitrators",
  6: "Daily Proceeding",
  7: "Cause List",
  8: "Reports",
  9: "Master",
  10: "Book Hearing Room",
  11: "Scrutiny",
  12: "Financial Dashboard",
  13: "Generate VC Link",
  14: "Email Dashboard",
  15: "Order Drafting",
  16: "Meeting Links",
  17: "Adjournments",
  18: "Reschedule",
};

function PanelBody({ selected, profile, bookingPrefill, tabRedirect }) {
  // Data handed over by navigateToAdminTab, for the tab it targeted only.
  const prefill =
    tabRedirect && String(tabRedirect.tab) === String(selected)
      ? tabRedirect.prefill
      : null;

  if (selected == 1 || selected == 0) {
    return <Overview profile={profile} />;
  }
  if (selected == 2) {
    return <CreateNewMatter />;
  }
  if (selected == 3) {
    return <Notices />;
  }
  if (selected == 4) {
    return <FeeCalculator />;
  }
  if (selected == 5) {
    return <Arbitrators profile={profile} />;
  }
  if (selected == 6) {
    return <DailyProceeding />;
  }
  if (selected == 7) {
    return <CauseList />;
  }
  if (selected == 8) {
    return <ReportContainer profile={profile} />;
  }
  if (selected == 9) {
    return <MasterCis />;
  }
  if (selected == 10) {
    return <BookHearingRoom prefill={bookingPrefill} />;
  }
  if (selected == 11) {
    return <Scrutiny pageSize={10} profile={profile} />;
  }
  if (selected == 12) {
    return <FinancialContainer profile={profile} />;
  }
  if (selected == 13) {
    return <GenerateVCLink profile={profile} />;
  }
  if (selected == 14) {
    return <EmailsDashboard />;
  }
  if (selected == 15) {
    return <OrderDrafting prefill={prefill} />;
  }
  if (selected == 16) {
    return <MeetingLinks />;
  }
  if (selected == 17) {
    return <AdjournmentsTab profile={profile} />;
  }
  if (selected == 18) {
    return <RescheduleTab profile={profile} />;
  }
  return null;
}

function DashboardContent({ selected, profile, bookingPrefill, tabRedirect }) {
  const classes = useStyles();
  const title = PANEL_TITLES[selected];
  const isOverview = selected == 0 || selected == 1;
  const userName = [profile?.user?.first_name, profile?.user?.last_name]
    .filter(Boolean)
    .join(" ");

  return (
    <Box className={classes.panel}>
      {title && (
        <Box className={classes.panelTitleRow}>
          <Typography variant="h1" component="h1" className={classes.panelTitle}>
            {title}
          </Typography>
          {isOverview && userName && (
            <Typography className={classes.panelUser}>{userName}</Typography>
          )}
        </Box>
      )}
      <PanelBody
        selected={selected}
        profile={profile}
        bookingPrefill={bookingPrefill}
        tabRedirect={tabRedirect}
      />
    </Box>
  );
}

function Sidebar({ can, selected, setSelected, setStep }) {
  const classes = useStyles();
  const history = useHistory();

  return (
    <Box className={classes.sidebar}>
      <Box height="1rem"></Box>
      <Typography className={classes.sidebarHeading}>Dashboard</Typography>

      <List style={{ paddingTop: "0" }}>
        {!can("case_overview") ? null : selected == 1 ? (
          <ListItem
            className={classes.selected}
            button
            onClick={() => {
              setSelected(1);
              setStep(1);
              removeSessionStorage();
              history.push("/");
            }}
          >
            <Typography
              className={classes.sidebarItem}
              style={{ color: TEXT.onDark }}
            >
              View Case
            </Typography>
          </ListItem>
        ) : (
          <ListItem
            button
            onClick={() => {
              setSelected(1);
              setStep(1);
              // history.push('/')
              removeSessionStorage();
              sessionStorage.setItem("tiac_sidebar_mobilestep", 1);
              sessionStorage.setItem("tiac_sidebar_step", 1);
            }}
          >
            <Typography className={classes.sidebarItem}>View Case</Typography>
          </ListItem>
        )}

        {!can("register_matter") ? null : selected == 2 ? (
          <ListItem
            className={classes.selected}
            button
            onClick={() => {
              setSelected(2);
              setStep(1);
            }}
          >
            <Typography
              className={classes.sidebarItem}
              style={{ color: TEXT.onDark }}
            >
              Register New Case
            </Typography>
          </ListItem>
        ) : (
          <ListItem
            button
            onClick={() => {
              setSelected(2);
              setStep(1);
              removeSessionStorage();
              sessionStorage.setItem("tiac_sidebar_step", 2);
              sessionStorage.setItem("tiac_sidebar_mobilestep", 1);
            }}
          >
            <Typography className={classes.sidebarItem}>
              Register New Case
            </Typography>
          </ListItem>
        )}

        {!can("scrutiny") ? null : selected == 11 ? (
          <ListItem
            className={classes.selected}
            button
            onClick={() => {
              setSelected(11);
              setStep(1);
            }}
          >
            <Typography
              className={classes.sidebarItem}
              style={{ color: TEXT.onDark }}
            >
              Scrutiny
            </Typography>
          </ListItem>
        ) : (
          <ListItem
            button
            onClick={() => {
              setSelected(11);
              setStep(1);
              removeSessionStorage();
              sessionStorage.setItem("tiac_sidebar_step", 11);
              sessionStorage.setItem("tiac_sidebar_mobilestep", 1);
            }}
          >
            <Typography className={classes.sidebarItem}>
              Scrutiny
            </Typography>
          </ListItem>
        )}

        {!can("notice") ? null : selected == 3 ? (
          <ListItem
            className={classes.selected}
            button
            onClick={() => {
              setSelected(3);
              setStep(1);
            }}
          >
            <Typography
              className={classes.sidebarItem}
              style={{ color: TEXT.onDark }}
            >
              Notices
            </Typography>
          </ListItem>
        ) : (
          <ListItem
            button
            onClick={() => {
              setSelected(3);
              setStep(1);
              removeSessionStorage();
              sessionStorage.setItem("tiac_sidebar_step", 3);
              sessionStorage.setItem("tiac_sidebar_mobilestep", 1);
            }}
          >
            <Typography className={classes.sidebarItem}>
              Notices
            </Typography>
          </ListItem>
        )}

        {!can("email_dashboard") ? null : selected == 14 ? (
          <ListItem
            className={classes.selected}
            button
            onClick={() => {
              setSelected(14);
              setStep(1);
            }}
          >
            <Typography
              className={classes.sidebarItem}
              style={{ color: TEXT.onDark }}
            >
              Email Dashboard
            </Typography>
          </ListItem>
        ) : (
          <ListItem
            button
            onClick={() => {
              setSelected(14);
              setStep(1);
              removeSessionStorage();
              sessionStorage.setItem("tiac_sidebar_step", 14);
              sessionStorage.setItem("tiac_sidebar_mobilestep", 1);
            }}
          >
            <Typography className={classes.sidebarItem}>
              Email Dashboard
            </Typography>
          </ListItem>
        )}

        {!can("fee_calculator") ? null : selected == 4 ? (
          <ListItem
            className={classes.selected}
            button
            onClick={() => {
              setSelected(4);
              setStep(1);
            }}
          >
            <Typography
              className={classes.sidebarItem}
              style={{ color: TEXT.onDark }}
            >
              Valuation of Fee
            </Typography>
          </ListItem>
        ) : (
          <ListItem
            button
            onClick={() => {
              setSelected(4);
              setStep(1);
              removeSessionStorage();
              sessionStorage.setItem("tiac_sidebar_step", 4);
              sessionStorage.setItem("tiac_sidebar_mobilestep", 1);
            }}
          >
            <Typography className={classes.sidebarItem}>
              Valuation of Fee
            </Typography>
          </ListItem>
        )}

        {!can("daily_proceedings") ? null : (selected == 6 ? (
          <ListItem
            className={classes.selected}
            button
            onClick={() => {
              setSelected(6);
              setStep(1);
            }}
          >
            <Typography
              className={classes.sidebarItem}
              style={{ color: TEXT.onDark }}
            >
              Daily Proceeding
            </Typography>
          </ListItem>
        ) : (
          <ListItem
            button
            onClick={() => {
              setSelected(6);
              setStep(1);
              removeSessionStorage();
              sessionStorage.setItem("tiac_sidebar_step", 6);
              sessionStorage.setItem("tiac_sidebar_mobilestep", 1);
            }}
          >
            <Typography className={classes.sidebarItem}>
              Daily Proceeding
            </Typography>
          </ListItem>
        ))}

        {!can("vc_link") ? null : selected == 13 ? (
          <ListItem
            className={classes.selected}
            button
            onClick={() => {
              setSelected(13);
              setStep(1);
            }}
          >
            <Typography
              className={classes.sidebarItem}
              style={{ color: TEXT.onDark }}
            >
              Generate VC Link
            </Typography>
          </ListItem>
        ) : (
          <ListItem
            button
            onClick={() => {
              setSelected(13);
              setStep(1);
              removeSessionStorage();
              sessionStorage.setItem("tiac_sidebar_step", 13);
              sessionStorage.setItem("tiac_sidebar_mobilestep", 1);
            }}
          >
            <Typography className={classes.sidebarItem}>
              Generate VC Link
            </Typography>
          </ListItem>
        )}

        {!can("meeting_links") ? null : selected == 16 ? (
          <ListItem
            className={classes.selected}
            button
            onClick={() => {
              setSelected(16);
              setStep(1);
            }}
          >
            <Typography
              className={classes.sidebarItem}
              style={{ color: TEXT.onDark }}
            >
              Meeting Links
            </Typography>
          </ListItem>
        ) : (
          <ListItem
            button
            onClick={() => {
              setSelected(16);
              setStep(1);
              removeSessionStorage();
              sessionStorage.setItem("tiac_sidebar_step", 16);
              sessionStorage.setItem("tiac_sidebar_mobilestep", 1);
            }}
          >
            <Typography className={classes.sidebarItem}>
              Meeting Links
            </Typography>
          </ListItem>
        )}

        {!can("order_drafting") ? null : selected == 15 ? (
          <ListItem
            className={classes.selected}
            button
            onClick={() => {
              setSelected(15);
              setStep(1);
            }}
          >
            <Typography
              className={classes.sidebarItem}
              style={{ color: TEXT.onDark }}
            >
              Order Drafting
            </Typography>
          </ListItem>
        ) : (
          <ListItem
            button
            onClick={() => {
              setSelected(15);
              setStep(1);
              removeSessionStorage();
              sessionStorage.setItem("tiac_sidebar_step", 15);
              sessionStorage.setItem("tiac_sidebar_mobilestep", 1);
            }}
          >
            <Typography className={classes.sidebarItem}>
              Order Drafting
            </Typography>
          </ListItem>
        )}

        {!can("book_hearing_room") ? null : selected == 10 ? (
          <ListItem
            className={classes.selected}
            button
            onClick={() => {
              setSelected(10);
              setStep(1);
            }}
          >
            <Typography
              className={classes.sidebarItem}
              style={{ color: TEXT.onDark }}
            >
              Book Hearing Room
            </Typography>
          </ListItem>
        ) : (
          <ListItem
            button
            onClick={() => {
              setSelected(10);
              setStep(1);
              removeSessionStorage();
              sessionStorage.setItem("tiac_sidebar_step", 10);
              sessionStorage.setItem("tiac_sidebar_mobilestep", 1);
            }}
          >
            <Typography className={classes.sidebarItem}>
              Book Hearing Room
            </Typography>
          </ListItem>
        )}

        {/* {!can("adjournment") ? null : selected == 17 ? (
          <ListItem
            className={classes.selected}
            button
            onClick={() => {
              setSelected(17);
              setStep(1);
            }}
          >
            <Typography
              className={classes.sidebarItem}
              style={{ color: TEXT.onDark }}
            >
              Adjournments
            </Typography>
          </ListItem>
        ) : (
          <ListItem
            button
            onClick={() => {
              setSelected(17);
              setStep(1);
              removeSessionStorage();
              sessionStorage.setItem("tiac_sidebar_step", 17);
              sessionStorage.setItem("tiac_sidebar_mobilestep", 1);
            }}
          >
            <Typography className={classes.sidebarItem}>Adjournments</Typography>
          </ListItem>
        )} */}

        {!can("adjournment") ? null : selected == 18 ? (
          <ListItem
            className={classes.selected}
            button
            onClick={() => {
              setSelected(18);
              setStep(1);
            }}
          >
            <Typography
              className={classes.sidebarItem}
              style={{ color: TEXT.onDark }}
            >
              Reschedule
            </Typography>
          </ListItem>
        ) : (
          <ListItem
            button
            onClick={() => {
              setSelected(18);
              setStep(1);
              removeSessionStorage();
              sessionStorage.setItem("tiac_sidebar_step", 18);
              sessionStorage.setItem("tiac_sidebar_mobilestep", 1);
            }}
          >
            <Typography className={classes.sidebarItem}>Reschedule</Typography>
          </ListItem>
        )}

        {!can("financial") ? null : selected == 12 ? (
          <ListItem
            className={classes.selected}
            button
            onClick={() => {
              setSelected(12);
              setStep(1);
            }}
          >
            <Typography
              className={classes.sidebarItem}
              style={{ color: TEXT.onDark }}
            >
              Financial
            </Typography>
          </ListItem>
        ) : (
          <ListItem
            button
            onClick={() => {
              setSelected(12);
              setStep(1);
              removeSessionStorage();
              sessionStorage.setItem("tiac_sidebar_step", 12);
              sessionStorage.setItem("tiac_sidebar_mobilestep", 1);
            }}
          >
            <Typography className={classes.sidebarItem}>
              Financial
            </Typography>
          </ListItem>
        )}

        {!can("arbitrator_tab") ? null : selected == 5 ? (
          <ListItem
            className={classes.selected}
            button
            onClick={() => {
              setSelected(5);
              setStep(1);
            }}
          >
            <Typography
              className={classes.sidebarItem}
              style={{ color: TEXT.onDark }}
            >
              Arbitrators
            </Typography>
          </ListItem>
        ) : (
          <ListItem
            button
            onClick={() => {
              setSelected(5);
              setStep(1);
              removeSessionStorage();
              sessionStorage.setItem("tiac_sidebar_step", 5);
              sessionStorage.setItem("tiac_sidebar_mobilestep", 1);
            }}
          >
            <Typography className={classes.sidebarItem}>
              Arbitrators
            </Typography>
          </ListItem>
        )}

        {can("cause_list") &&
          (selected == 7 ? (
            <ListItem
              className={classes.selected}
              button
              onClick={() => {
                setSelected(7);
                setStep(1);
              }}
            >
              <Typography
                className={classes.sidebarItem}
                style={{ color: TEXT.onDark }}
              >
                Cause List
              </Typography>
            </ListItem>
          ) : (
            <ListItem
              button
              onClick={() => {
                setSelected(7);
                setStep(1);
                removeSessionStorage();
                sessionStorage.setItem("tiac_sidebar_step", 7);
                sessionStorage.setItem("tiac_sidebar_mobilestep", 1);
              }}
            >
              <Typography className={classes.sidebarItem}>
                Cause List
              </Typography>
            </ListItem>
          ))}

        {!can("reports") ? null : (selected == 8 ? (
          <ListItem
            className={classes.selected}
            button
            onClick={() => {
              setSelected(8);
              setStep(1);
            }}
          >
            <Typography
              className={classes.sidebarItem}
              style={{ color: TEXT.onDark }}
            >
              Reports
            </Typography>
          </ListItem>
        ) : (
          <ListItem
            button
            onClick={() => {
              setSelected(8);
              setStep(1);
              removeSessionStorage();
              sessionStorage.setItem("tiac_sidebar_step", 8);
              sessionStorage.setItem("tiac_sidebar_mobilestep", 1);
            }}
          >
            <Typography className={classes.sidebarItem}>
              Reports
            </Typography>
          </ListItem>
        ))}

        {!can("master_tab") ? null : selected == 9 ? (
          <ListItem
            className={classes.selected}
            button
            onClick={() => {
              setSelected(9);
              setStep(1);
            }}
          >
            <Typography
              className={classes.sidebarItem}
              style={{ color: TEXT.onDark }}
            >
              Master
            </Typography>
          </ListItem>
        ) : (
          <ListItem
            button
            onClick={() => {
              setSelected(9);
              setStep(1);
              removeSessionStorage();
              sessionStorage.setItem("tiac_sidebar_step", 9);
              sessionStorage.setItem("tiac_sidebar_mobilestep", 1);
            }}
          >
            <Typography className={classes.sidebarItem}>Master</Typography>
          </ListItem>
        )}
      </List>
    </Box>
  );
}

function Body({ hidden = false, selected, profile, setSelected, setStep, bookingPrefill, tabRedirect }) {
  const classes = useStyles();

  return (
    <Box className={classes.body2} style={hidden ? { display: "none" } : undefined}>
      <Hidden mdUp>
        <Paper
          style={{
            background: "none",
            border: "none",
            boxShadow: "none",
            display: "flex",
            justifyContent: "flex-start",
            padding: "15px 20px",
          }}
        >
          <Button
            size="large"
            onClick={(e) => {
              setStep(0);
              setSelected(0);
              removeSessionStorage();
            }}
            startIcon={<ArrowBackIosIcon />}
          >
            <FormattedMessage id="buttons.back" />
          </Button>
        </Paper>
      </Hidden>
      <Box className={classes.body}>
        <DashboardContent
          selected={selected}
          profile={profile}
          bookingPrefill={bookingPrefill}
          tabRedirect={tabRedirect}
        />
      </Box>
    </Box>
  );
}
